import React, { useEffect, useState } from "react";
import Scheduler from "devextreme-react/scheduler";
import CustomStore from "devextreme/data/custom_store";
import { fetchAllAppointments, createAppointment, updateAppointment, deleteAppointment, fetchAllWorkcells } from "../api";
import { Box, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import FullWidthLayout from '../components/FullWidthLayout';
import { useNavigate, useParams } from "react-router-dom";
import {
  diffSchedulerDateTimeHours,
  formatLocalDate,
  formatSchedulerDate,
  formatSchedulerTime,
  parseAppointmentDates,
  parseSchedulerDateTime
} from "../utils/localDateTime";

const getColor = (i) => {
  const palette = [
    "#1e90ff", "#ff6347", "#32cd32", "#ffa500", "#8a2be2", "#ff1493", "#00ced1", "#ffd700"
  ];
  return palette[i % palette.length];
};

const WorkcellAppointmentsPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { workcell_id } = useParams();

  // Get date from URL or default to today
  const urlDate = params.date;
  const initialDate = urlDate ? parseSchedulerDateTime(`${urlDate}T00:00:00`) : parseSchedulerDateTime(formatLocalDate(new Date()));
  const initialDateStr = formatSchedulerDate(initialDate);

  const [workcells, setWorkcells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(initialDate);

  // Set selectedWorkcell from URL param
  const selectedWorkcell = workcell_id ? parseInt(workcell_id) : (workcells[0]?.id || "");
  const selectedWorkcellObj = workcells.find(w => w.id === selectedWorkcell);

  useEffect(() => {
      fetchAllWorkcells()
        .then(data => {
          // Only include workcells with has_oee true
          const filtered = data.filter(w => w.has_oee);
          const workcellList = filtered.map((w, i) => ({
            id: w.workcell_id,
            text: w.workcell_name,
            color: getColor(i)
          }));
          setWorkcells(workcellList);
          // If no workcell_id in URL, redirect to first workcell
          if (!workcell_id && workcellList.length > 0) {
            navigate(`/workcell-appointments/${workcellList[0].id}/${initialDateStr}`, { replace: true });
          }
        })
        .finally(() => setLoading(false));
    }, [workcell_id, initialDateStr, navigate]);

  const appointmentsStore = new CustomStore({
      key: "id",
      load: () =>
        fetchAllAppointments(selectedWorkcell).then(data => {
          console.log('[appointmentsStore] data:', data);
          return (data || []).map(parseAppointmentDates);
        }),
      insert: createAppointment,
      update: updateAppointment,
      remove: deleteAppointment,
    });

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  // const filteredWorkcells = workcells.filter(w => enabledWorkcells.includes(w.id));

  return (
    <FullWidthLayout>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="stretch"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto'
        }}
        style={{ position: "relative" }}
      >
        <Box display="flex" alignItems="center" mb={2} mt={1}>
          <FormControl sx={{ minWidth: 260, mr: 2 }}>
            <InputLabel id="workcell-select-label">Workcell</InputLabel>
            <Select
              labelId="workcell-select-label"
              value={selectedWorkcell || ""}
              label="Workcell"
              onChange={e => navigate(`/workcell-appointments/${e.target.value}/${initialDateStr}`)}
            >
              {workcells.map(w => (
                <MenuItem key={w.id} value={w.id} style={{ color: w.color }}>{w.text}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="h5" mb={2}>Workcell calendar</Typography>
        </Box>
        {/* Scheduler */}
        <Box flex={1} width="100%" p={0} m={0} height="100%">
          <Box mb={2} style={{ position: 'absolute', right: 0, top: 0, marginTop: "0" }}>
            <a href="/workcell-appointments-month" style={{ textDecoration: 'none' }}>
              <button style={{
                background: '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '6px 16px',
                fontSize: '1rem',
                cursor: 'pointer',
                fontWeight: 500
              }}>
                Monthly view
              </button>
            </a>
          </Box>
          <Scheduler
            dataSource={appointmentsStore}
            views={[
              { type: "day", intervalCount: 7, startDayHour: 6, endDayHour: 20 },
              // { type: "timelineDay", intervalCount: 1, startDayHour: 6, endDayHour: 20, cellDuration: 120 }
            ]}
            currentView="day"
            currentDate={currentDate}
            onCurrentDateChange={setCurrentDate}
            timeZone="UTC"
            startDayHour={0}
            endDayHour={24}
            cellDuration={30}
            editing={{
              allowAdding: true,
              allowUpdating: true,
              allowDeleting: true,
              allowResizing: true,
              allowDragging: true,
              useConfirmationDialog: false
            }}
            resources={[{
              fieldExpr: "workcellId",
              dataSource: selectedWorkcellObj ? [selectedWorkcellObj] : [],
              label: "Workcell",
              useColorAsDefault: true,
              colorExpr: "color"
            }]}
            appointmentRender={({ appointmentData }) => {
              const workcell = workcells.find(w => w.id === appointmentData.workcellId);
              const startHour = formatSchedulerTime(appointmentData.startDate);
              const durationHours = diffSchedulerDateTimeHours(appointmentData.startDate, appointmentData.endDate);
              const durationH = Math.floor(durationHours);
              const durationM = Math.round((durationHours - durationH) * 60);
              const durationStr = durationH > 0 ? `${durationH}h${durationM ? ` ${durationM}m` : ''}` : `${durationM}m`;
              return (
                <div style={{
                  background: workcell?.color || "#1e90ff",
                  color: "#fff",
                  padding: 4,
                  borderRadius: 4,
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  fontSize: "0.95em",
                  textAlign: "center"
                }}>
                  <div>{workcell?.text}</div>
                  <div>{startHour} ({durationStr})</div>
                </div>
              );
            }}
            showAllDayPanel={false}
          />
        </Box>



      </Box>
    </FullWidthLayout>
  );
};

export default WorkcellAppointmentsPage;
